import { authedFetch } from '../../lib/authedFetch';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, X, Zap, CreditCard, AlertTriangle } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../hooks/use-toast';

const THRESHOLD_DAYS = 3;
const SESSION_DISMISS_KEY = 'trial_banner_dismissed_at';
const REDISMISS_AFTER_MS = 12 * 60 * 60 * 1000; // re-show after 12h even if dismissed

/**
 * TrialBanner — sticky top bar shown to admins on trial whose trial_end_at is
 * within 3 days. Friendly countdown + one-click upgrade route to /business/billing.
 *
 * Renders nothing for non-trial tenants, non-admins, or when more than 3 days remain.
 * Dismissible per browser session, but re-appears every 12h until trial ends.
 */
export const TrialBanner = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { currentBusiness } = useBusiness();
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [portalPending, setPortalPending] = useState(false);

  // Tick every hour so the days-left figure stays accurate without reload
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Honour the per-session dismissal but re-show after 12h
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_DISMISS_KEY);
      if (stored && Date.now() - Number(stored) < REDISMISS_AFTER_MS) {
        setDismissed(true);
      }
    } catch (e) { /* sessionStorage might be unavailable */ }
  }, []);

  const { showBanner, daysLeft, hoursLeft, expired, frozen, pastDue, graceDays, graceHours, attempts, maxAttempts } = useMemo(() => {
    const status = currentBusiness?.subscription_status;
    const endIso = currentBusiness?.trial_end_at;
    const graceIso = currentBusiness?.grace_period_end_at;

    // Frozen / cancelled accounts always see the banner (permanent reactivation CTA)
    if (status === 'frozen' || status === 'cancelled') {
      return { showBanner: true, daysLeft: 0, hoursLeft: 0, expired: true, frozen: true,
        pastDue: false, graceDays: 0, graceHours: 0, attempts: 0, maxAttempts: 0 };
    }

    // Past-due (failed payment, in grace window) — amber banner with countdown
    if (status === 'past_due') {
      const graceTs = graceIso ? new Date(graceIso).getTime() : 0;
      const msLeft = graceTs ? graceTs - now : 0;
      const days = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
      const hours = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60)));
      return { showBanner: true, daysLeft: 0, hoursLeft: 0, expired: false, frozen: false,
        pastDue: true, graceDays: days, graceHours: hours,
        attempts: currentBusiness?.dunning_attempts || 0,
        maxAttempts: currentBusiness?.dunning_max_attempts || 2 };
    }

    if (status !== 'trial' || !endIso) {
      return { showBanner: false, daysLeft: 0, hoursLeft: 0, expired: false, frozen: false,
        pastDue: false, graceDays: 0, graceHours: 0, attempts: 0, maxAttempts: 0 };
    }
    const endTs = new Date(endIso).getTime();
    if (Number.isNaN(endTs)) return { showBanner: false, daysLeft: 0, hoursLeft: 0, expired: false,
      frozen: false, pastDue: false, graceDays: 0, graceHours: 0, attempts: 0, maxAttempts: 0 };
    const msLeft = endTs - now;
    const expiredFlag = msLeft <= 0;
    const days = expiredFlag ? 0 : Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    const hours = expiredFlag ? 0 : Math.ceil(msLeft / (1000 * 60 * 60));
    return {
      showBanner: expiredFlag || days <= THRESHOLD_DAYS,
      daysLeft: days,
      hoursLeft: hours,
      expired: expiredFlag,
      frozen: false,
      pastDue: false,
      graceDays: 0,
      graceHours: 0,
      attempts: 0,
      maxAttempts: 0,
    };
  }, [currentBusiness?.subscription_status, currentBusiness?.trial_end_at,
      currentBusiness?.grace_period_end_at, currentBusiness?.dunning_attempts,
      currentBusiness?.dunning_max_attempts, now]);

  // Frozen state must NOT be dismissible — it blocks productive use of the app.
  // Past-due is dismissible (per session) but reappears every 12h.
  if (!showBanner) return null;
  if (dismissed && !frozen) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, String(Date.now())); } catch (e) { /* noop */ }
    setDismissed(true);
  };

  // Past-due CTA opens Stripe customer portal so admin can update card immediately
  const handleUpdatePayment = async () => {
    if (!currentBusiness?.brandId) {
      navigate('/business/billing');
      return;
    }
    setPortalPending(true);
    try {
      // Resolve tenant_id from brand
      const tRes = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/by-brand/${currentBusiness.brandId}`);
      if (!tRes.ok) throw new Error(t('st.trial.errResolveTenant'));
      const tenant = await tRes.json();
      const portalRes = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${tenant.id}/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: `${window.location.origin}/business/billing` }),
      });
      if (!portalRes.ok) {
        const err = await portalRes.json().catch(() => ({}));
        throw new Error(err.detail || t('st.trial.errPortalUnavailable'));
      }
      const data = await portalRes.json();
      window.location.href = data.url;
    } catch (e) {
      toast({ title: t('st.trial.stripeFail'), description: t('st.trial.stripeFailDesc', { message: e.message }), variant: 'destructive' });
      navigate('/business/billing');
    } finally {
      setPortalPending(false);
    }
  };

  let headline;
  let sub;
  let bg;
  let ctaLabel;
  let ctaHandler;
  let Icon;
  let testidSuffix;

  if (frozen) {
    headline = t('st.trial.frozenHeadline');
    sub = t('st.trial.frozenSub');
    bg = 'from-slate-700 via-gray-700 to-zinc-700';
    ctaLabel = t('st.trial.reactivate');
    ctaHandler = () => navigate('/business/billing');
    Icon = Zap;
    testidSuffix = 'frozen';
  } else if (pastDue) {
    const remainder = graceDays > 1
      ? t('st.trial.nDays', { count: graceDays })
      : graceDays === 1
        ? t('st.trial.nHours', { count: graceHours })
        : (graceHours === 1 ? t('st.trial.nHour', { count: graceHours }) : t('st.trial.nHours', { count: graceHours }));
    headline = t('st.trial.pastDueHeadline', { remainder });
    sub = t('st.trial.pastDueSub', { attempt: Math.min(attempts, maxAttempts), max: maxAttempts });
    bg = 'from-amber-500 via-orange-500 to-amber-600';
    ctaLabel = portalPending ? t('st.trial.openingStripe') : t('st.trial.updatePayment');
    ctaHandler = handleUpdatePayment;
    Icon = AlertTriangle;
    testidSuffix = 'past-due';
  } else if (expired) {
    headline = t('st.trial.expiredHeadline');
    sub = t('st.trial.expiredSub');
    bg = 'from-red-600 via-rose-600 to-orange-500';
    ctaLabel = t('st.trial.upgradeNow');
    ctaHandler = () => navigate('/business/billing');
    Icon = Clock;
    testidSuffix = 'trial-expired';
  } else if (daysLeft <= 1) {
    headline = hoursLeft === 1 ? t('st.trial.urgentHeadlineOne', { count: hoursLeft }) : t('st.trial.urgentHeadline', { count: hoursLeft });
    sub = t('st.trial.soonSub');
    bg = 'from-orange-500 via-amber-500 to-yellow-500';
    ctaLabel = t('st.trial.upgradeNow');
    ctaHandler = () => navigate('/business/billing');
    Icon = Clock;
    testidSuffix = 'trial-urgent';
  } else {
    headline = t('st.trial.soonHeadline', { count: daysLeft });
    sub = t('st.trial.soonSub');
    bg = 'from-emerald-500 via-teal-500 to-blue-500';
    ctaLabel = t('st.trial.upgradeNow');
    ctaHandler = () => navigate('/business/billing');
    Icon = Clock;
    testidSuffix = 'trial-soon';
  }

  return (
    <div
      data-testid="trial-banner"
      data-banner-state={testidSuffix}
      className={`relative w-full bg-gradient-to-r ${bg} text-white shadow-md`}
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" data-testid="trial-banner-headline">
              {headline}
            </div>
            <div className="text-xs text-white/85 truncate hidden sm:block">{sub}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={ctaHandler}
            disabled={portalPending}
            data-testid="trial-banner-upgrade-btn"
            className="bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-70 font-semibold text-sm px-4 py-1.5 rounded-md transition shadow-sm flex items-center gap-1.5"
          >
            {pastDue && <CreditCard className="h-3.5 w-3.5" />}
            {ctaLabel}
          </button>
          {!frozen && (
            <button
              type="button"
              aria-label={t('st.trial.dismiss')}
              onClick={handleDismiss}
              data-testid="trial-banner-dismiss-btn"
              className="text-white/80 hover:text-white p-1 rounded transition"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;